import { Button, WarningCallout } from "nhsuk-react-components"
import { useContext, useEffect } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useAppointmentQuery } from "../../api/fetchAppointment"
import { useHealthcareServiceQuery } from "../../api/fetchHealthcareService"
import { useQuestionnaireQuery } from "../../api/fetchQuestionnaire"
import { InformationBlock } from "../../components/informationBlock/InformationBlock"
import { Loading } from "../../components/loading/Loading"
import { OtherWaysTo } from "../../components/otherWaysTo/OtherWaysTo"
import { AuthenticationContext } from "../../context/AuthenticationContext"
import { BackButtonContext } from "../../context/BackButtonContext"
import { QuestionnaireContext } from "../../context/QuestionnaireContext"
import {
  getCancellationQuestionnaireReference,
  getRescheduleQuestionnaireReference,
} from "../../fhirUtils/healthcareServiceUtils"
import { useNhsAppJs } from "../../hooks/useNhsAppJs"
import { AppointmentCard } from "../appointment/appointmentViews/AppointmentCard"
import { QuestionnaireComplete } from "./QuestionnaireComplete"

import type { HealthcareService, Location } from "fhir/r3"
import type { SimplifiedAppointment } from "../../appointment.types"
import type { PageFunction } from "../../types"
import { useLocationQuery } from "../../api/fetchLocation"

const WarningCalloutContent = ({
  healthcareService,
  pageFunction,
}: {
  healthcareService: HealthcareService
  pageFunction?: PageFunction
}) => {
  const { appointmentId, odsId } = useParams()

  const cancellationQuestionnaireId = getCancellationQuestionnaireReference(healthcareService).split("/")[1]
  const rescheduleQuestionnaireId = getRescheduleQuestionnaireReference(healthcareService).split("/")[1]

  if (pageFunction === "cancel") {
    return (
      <>
        <p>
          Cancelling this appointment may discharge you from care. You will no longer be on any waiting list for this
          care.
        </p>
        <p>Only cancel your appointment if you are sure you do not need this care.</p>
        <p>If you are not sure, contact your care provider.</p>
        <p>
          If you cannot attend the appointment but still need this care you can{" "}
          <Link to={`/appointment/${odsId}/${appointmentId}/reschedule/${rescheduleQuestionnaireId}`}>
            change it instead.
          </Link>
        </p>
      </>
    )
  }
  if (pageFunction === "reschedule") {
    return (
      <>
        <p>You should only reschedule your appointment if you are sure you cannot attend at the scheduled time.</p>
        <p>If you are not sure, contact your care provider.</p>
        <p>
          If you no longer need this appointment you should{" "}
          <Link to={`/appointment/${odsId}/${appointmentId}/cancel/${cancellationQuestionnaireId}`}>
            cancel it instead.
          </Link>
        </p>
      </>
    )
  }
  return null
}

type QuestionnaireStartContentProps = {
  appointment: SimplifiedAppointment
  healthcareService: HealthcareService
  location?: Location
  pageFunction: PageFunction
  onStart: () => void
}

const HOW_IT_WORKS_BULLETS: Record<PageFunction, string[]> = {
  cancel: [
    "The booking team will review and process your request in approximately 2 working days.",
    "The cancelled appointment will remain visible until the date has passed.",
    "The booking team will contact you if your cancellation is not accepted.",
  ],
  reschedule: [
    "The booking team will review and process your request in approximately 2 working days.",
    "The old date and time may no longer be available after you make the request.",
    "The rescheduled appointment will remain visible until the date has passed.",
  ],
}

export const QuestionnaireStartContent = ({
  appointment,
  healthcareService,
  location,
  pageFunction,
  onStart,
}: QuestionnaireStartContentProps) => {
  const howItWorksBullets = HOW_IT_WORKS_BULLETS[pageFunction]

  const pageHeader = `Ask to ${pageFunction} your appointment`
  return (
    <>
      <h1 className="appointment-header">{pageHeader}</h1>
      <AppointmentCard
        appointment={appointment}
        healthcareService={healthcareService}
        location={location}
        appointmentView
      />

      <InformationBlock header="How it works" information={howItWorksBullets} />

      <WarningCallout>
        <WarningCallout.Label>Important</WarningCallout.Label>
        <WarningCalloutContent healthcareService={healthcareService} pageFunction={pageFunction} />
      </WarningCallout>

      <InformationBlock
        header="Before you start"
        information={[
          `Make sure you have chosen the right appointment to ${pageFunction}.`,
          "We will ask you some questions about the appointment to help us process your request.",
          `If you are not the patient, you must get the patient's consent to ${pageFunction} this appointment.`,
        ]}
      />

      <Button onClick={onStart}>Start</Button>
      <OtherWaysTo header={`Other ways to ${pageFunction}`} healthcareService={healthcareService} location={location} />
    </>
  )
}

/**
 * Questionnaire Flow:
 *
 * `QuestionnaireStart.tsx` -> (`QuestionPage.tsx` -> `...` -> `QuestionPage.tsx`) -> `QuestionnaireSummary.tsx`
 *
 * This page:
 * 1. Fetches required data (questionnaire, appointment, healthcare service) using queries.
 * 2. Sets up back button handler based on patient action (reschedule or cancel).
 * 3. Starts the questionnaire on button click.
 *
 * @returns {JSX.Element} The rendered page.
 */
export const QuestionnaireStart = () => {
  // Navigation
  const params = useParams()
  const navigate = useNavigate()
  const { setOnBackPress, setShowBackButton } = useContext(BackButtonContext)

  const { token } = useContext(AuthenticationContext)
  const { navigateToPage } = useNhsAppJs()

  // Questionnaire Context
  const questionnaireContext = useContext(QuestionnaireContext)
  const { changeRequested } = useContext(AuthenticationContext)

  if (!questionnaireContext) {
    throw new Error("QuestionnaireStart must be used within a QuestionnaireProvider")
  }
  const { startQuestionnaire, pageFunction } = questionnaireContext

  const questionnaireQuery = useQuestionnaireQuery({
    odsId: params.odsId,
    questionnaireId: params.questionnaireId,
    token,
  })

  const appointmentQuery = useAppointmentQuery({
    appointmentId: params.appointmentId,
    token,
  })
  const appointment = appointmentQuery.data
  const locationId =
    appointment?.participantLocationReference?.length && appointment?.participantLocationReference?.length > 0
      ? appointment.participantLocationReference[0]?.reference?.split("Location/")[1]
      : undefined
  const healthcareServiceQuery = useHealthcareServiceQuery({
    odsId: params.odsId,
    token,
  })

  useEffect(() => {
    if (setOnBackPress && setShowBackButton) {
      setShowBackButton(true)
      setOnBackPress("back")
    }
  }, [navigate, pageFunction, setOnBackPress, setShowBackButton])

  useEffect(() => {
    if (pageFunction) {
      document.title = `Ask to ${pageFunction} your appointment`
    }
  }, [pageFunction])

  const { data: location } = useLocationQuery({ locationId, token })

  if (changeRequested) return <QuestionnaireComplete />

  if (appointmentQuery.isLoading || healthcareServiceQuery.isLoading || questionnaireQuery.isLoading) {
    return <Loading />
  }

  if (questionnaireQuery.isError || appointmentQuery.isError || healthcareServiceQuery.isError) {
    navigateToPage("error")
  }

  const healthcareService = healthcareServiceQuery.data
  const questionnaire = questionnaireQuery.data

  if (!healthcareService || !appointment || !questionnaire || !pageFunction) {
    navigateToPage("error")
    return null
  }

  return (
    <QuestionnaireStartContent
      appointment={appointment}
      healthcareService={healthcareService}
      pageFunction={pageFunction}
      onStart={() => {
        startQuestionnaire(questionnaire)
        navigate("page/1")
      }}
      location={location}
    />
  )
}
